import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Shield, Key, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCNPJ = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0,2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8)}`;
  return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12)}`;
};

export default function LicenseActivationPage({ onActivated }) {
  const [cnpj, setCnpj] = useState('');
  const [activationKey, setActivationKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/license/activate`, {
        cnpj: cnpj.replace(/\D/g, ''),
        activation_key: activationKey.trim()
      });
      setSuccess(res.data);
      toast.success('Licenca ativada com sucesso!');
      setTimeout(() => {
        if (onActivated) onActivated();
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao ativar licenca');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-teal-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Licenca Ativada!</h2>
          <p className="text-slate-500 mb-4">{success.client_name}</p>
          <div className="bg-slate-50 rounded-lg p-4 text-left space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Plano:</span>
              <span className="text-sm font-medium">{success.plan}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Validade:</span>
              <span className="text-sm font-medium">{new Date(success.expiration_date).toLocaleDateString('pt-BR')}</span>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-4">Redirecionando para o login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-teal-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-teal-700" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">ConsulTrack</h1>
          <p className="text-slate-500 mt-1">Ativacao de Licenca</p>
          <p className="text-xs text-slate-400 mt-2">
            Insira o CNPJ e a chave de ativacao fornecidos pela HM Solucoes em TI
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">CNPJ</label>
            <Input
              value={cnpj}
              onChange={(e) => setCnpj(formatCNPJ(e.target.value))}
              placeholder="00.000.000/0000-00"
              required
              data-testid="activation-cnpj-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Chave de Ativacao</label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={activationKey}
                onChange={(e) => setActivationKey(e.target.value.toUpperCase())}
                placeholder="CT-XXXXXXXX-XXXXXX"
                className="pl-10 font-mono"
                required
                data-testid="activation-key-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            className="w-full bg-teal-700 hover:bg-teal-800"
            disabled={loading}
            data-testid="activation-submit-btn"
          >
            {loading ? 'Ativando...' : 'Ativar Licenca'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-slate-400">
            HM Solucoes em TI - CNPJ: 65.780.906/0001-11
          </p>
        </div>
      </div>
    </div>
  );
}
